import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import Logger from '../utils/logger';

const initial = {
  eventsListState: [],
  marketingPageName: '',
  experientialDateLimit: '',
  experientialTimeLimit: '',
  gdaEventsHideStatus: true,
  activeSection: null,
  resyCreditCardTypes: [],
};

export const getQueryVariable = (variable) => {
  const query = window.location.search.substring(1);
  const vars = query.split('&');

  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');

    if (pair[0] === variable) {
      return decodeURIComponent(pair[1]);
    }
  }
  return (false);
};

const fetchEventsDataThunk = createAsyncThunk(
  'events/fetchGDAEvents',
  async ({
    baseRequestUrl,
    gdaEventsmaxCount,
  }) => {
    const assetsEndpoint = `${baseRequestUrl}GDAEvents/GDAEventsData.json`;
    const response = await fetch(assetsEndpoint, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'Access-Control-Allow-Origin': baseRequestUrl,
      },
      mode: 'cors',
    });
    const eventsData = await response.json();
    const finalResponse = {
      baseRequestUrl,
      gdaEventsmaxCount,
      eventsData,
    };
    return finalResponse;
  }
);

const massageEventsData = (baseRequestUrl,
  gdaEventsmaxCount, eventsData) => {
  moment.tz.setDefault('America/New_York');
  let eventsListUpdate = [];
  const now = moment();
  const currentDateFullInEST = now.format('DD-MM-YYYY HH:mm');
  try {
    const selectedList = eventsData.events;
    // images are stored in S3 now. Use baseRequestUrl in the image path
    const eventsListComplete = selectedList.map((event) => ({
      city: event.city,
      displayEventEndDate: event.displayEventEndDate,
      displayEventEndTime: event.displayEventEndTime,
      displayEventStartDate: event.displayEventStartDate,
      displayEventStartTime: event.displayEventStartTime,
      eventCategory: event.eventCategory,
      eventImageDatePreview: event.eventImageDatePreview,
      eventName: event.eventName,
      eventURL: event.eventURL ? event.eventURL : '',
      imageName: `${baseRequestUrl}GDAEvents/${event.imageName}`,
      tileTimingDetails: event.tileTimingDetails,
      venueName: event.venueName,
      id: event.id,
    }));

    // if showAll parameter is true, then gda_events_maxcount will be ignore.
    // if show all is not true or not present  , we need to filter events based on the system date and event dates then show the events based on gda max count.
    let isShowAll = '';
    if (ENV !== 'prod') {
      isShowAll = getQueryVariable('showAll');
      if (isShowAll === 'true') {
        eventsListUpdate = eventsListComplete;
      } else {
        isShowAll = '';
      }
    }

    if (isShowAll === '') {
      const eventsList = eventsListComplete.map((event) => {
        const endDate = event.displayEventEndDate;
        const endTime = event.displayEventEndTime;
        const startDate = event.displayEventStartDate;
        const startTime = event.displayEventStartTime;
        const parseCurrentDate = moment(currentDateFullInEST, 'DD-MM-YYYY HH:mm');
        // eslint-disable-next-line prefer-template
        const parsedEndDate = moment(endDate + ' ' + endTime, 'DD-MM-YYYY HH:mm');
        // eslint-disable-next-line prefer-template
        const parsedStartDate = moment(startDate + ' ' + startTime, 'DD-MM-YYYY HH:mm');
        if (parseCurrentDate >= parsedStartDate && parseCurrentDate <= parsedEndDate) {
          return event;
        }
        return null;
      });
      const filteredEventsList = eventsList.filter((x) => (x !== null));
      let updatedResponse = [];
      if (gdaEventsmaxCount !== -1 && (filteredEventsList.length > gdaEventsmaxCount)) {
        updatedResponse = filteredEventsList.slice(0, gdaEventsmaxCount);
      } else {
        updatedResponse = filteredEventsList;
      }
      eventsListUpdate = updatedResponse;
    }
  } catch (err) {
    // error handling
    Logger.error(err);
  }
  return eventsListUpdate;
};

const handleError = (e = {}) => {
  const { message = '' } = e;
  Logger.error(`[ERROR] GDA Event : load, ${message}`);
};

const amexEventsSlice = createSlice({
  name: 'gdaEvents',
  initialState: initial,
  reducers: {
    setMarketingPageName: (state, action) => {
      const pageName = action.payload;
      state.marketingPageName = pageName;
    },
    setExperientialDateLimit: (state, action) => {
      const dateLimit = action.payload;
      state.experientialDateLimit = dateLimit;
    },
    setExperientialTimeLimit: (state, action) => {
      const timeLimit = action.payload;
      state.experientialTimeLimit = timeLimit;
    },
    setGDAEventsHideStatus: (state, action) => {
      state.gdaEventsHideStatus = action.payload;
    },
    setActiveSection: (state, action) => {
      state.activeSection = action.payload;
    },
    setResyCreditCardTypes: (state, action) => {
      state.resyCreditCardTypes = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEventsDataThunk.fulfilled, (state, action) => {
      const {
        baseRequestUrl, gdaEventsmaxCount, eventsData,
      } = action.payload;
      const eventList = massageEventsData(baseRequestUrl, gdaEventsmaxCount, eventsData);
      if (eventList && eventList.length > 0) {
        state.eventsListState = eventList;
      } else {
        state.eventsListState = [];
      }
    });
    builder.addCase(fetchEventsDataThunk.rejected, (state, action) => {
      handleError(action.error);
      state.eventsListState = [];
    });
  },
});

const {
  setMarketingPageName,
  setExperientialDateLimit, setExperientialTimeLimit, setGDAEventsHideStatus, setActiveSection, setResyCreditCardTypes,
} = amexEventsSlice.actions;

const finalEventsData = (baseRequestUrl, gdaEventsmaxCount) => (dispatch) => dispatch(fetchEventsDataThunk({ baseRequestUrl, gdaEventsmaxCount }));

export {
  finalEventsData, fetchEventsDataThunk, setMarketingPageName, setExperientialDateLimit, setExperientialTimeLimit, setGDAEventsHideStatus, setActiveSection, setResyCreditCardTypes,
};
export default amexEventsSlice.reducer;
